import React, { useEffect, useMemo, useState } from 'react';
import BasketballLiveCard from '../components/BasketballLiveCard';

const API_URL = '/api/basketball/live';
const REFRESH_MS = 30000;
const FINISHED_GRACE_MS = 60000;

const normalizeCards = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.basketballCards)) return payload.basketballCards;
  if (Array.isArray(payload?.cards)) return payload.cards;
  if (Array.isArray(payload?.matches)) return payload.matches;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const isFinishedCard = (card) => {
  const text = `${card?.status || ''} ${card?.matchStatus || ''} ${card?.gameStatus || ''} ${card?.statusShort || ''}`.toLowerCase();
  return Boolean(card?.isFinished) || ['finished', 'final', 'ended', 'ft', 'aot', 'maç sonu'].some((x) => text.includes(x));
};

const getCardTime = (card) => Number(card?.timestamp || card?.date?.timestamp || 0);
const getCardConfidence = (card) => Number(card?.confidence || card?.analysis?.confidence || 0);

const BasketballLive = () => {
  const [cards, setCards] = useState([]);
  const [finishedSeenAt, setFinishedSeenAt] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchLiveCards = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError('');

      const response = await fetch(`${API_URL}?t=${Date.now()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Canlı basketbol servisi cevap vermedi. HTTP ${response.status}`);
      }

      const payload = await response.json();
      const nextCards = normalizeCards(payload);
      const now = Date.now();

      setFinishedSeenAt((prev) => {
        const next = { ...prev };
        const activeIds = new Set();

        nextCards.forEach((card, index) => {
          const id = String(card?.gameId || card?.id || `${card?.homeTeam}-${card?.awayTeam}-${index}`);
          activeIds.add(id);
          if (isFinishedCard(card) && !next[id]) next[id] = now;
          if (!isFinishedCard(card) && next[id]) delete next[id];
        });

        Object.keys(next).forEach((id) => {
          if (!activeIds.has(id)) delete next[id];
        });

        return next;
      });

      setCards(nextCards);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.message || 'Canlı basketbol verisi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveCards(false);
    const interval = setInterval(() => fetchLiveCards(true), REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const visibleCards = useMemo(() => {
    const now = Date.now();

    return [...cards]
      .filter((card, index) => {
        const id = String(card?.gameId || card?.id || `${card?.homeTeam}-${card?.awayTeam}-${index}`);
        if (!isFinishedCard(card)) return true;
        const seenAt = finishedSeenAt[id] || now;
        return now - seenAt <= FINISHED_GRACE_MS;
      })
      .sort((a, b) => {
        if (Number(b?.isLive) !== Number(a?.isLive)) return Number(b?.isLive) - Number(a?.isLive);
        if (Number(a?.isFinished) !== Number(b?.isFinished)) return Number(a?.isFinished) - Number(b?.isFinished);
        if (getCardConfidence(b) !== getCardConfidence(a)) return getCardConfidence(b) - getCardConfidence(a);
        return getCardTime(a) - getCardTime(b);
      });
  }, [cards, finishedSeenAt]);

  return (
    <section className="basketball-live-page">
      <div className="basketball-live-page__header">
        <div>
          <span className="basketball-live-page__eyebrow">Canlı Basketbol</span>
          <h1>Canlı Analiz</h1>
          <p>Aktif basketbol maçları, momentum ve canlı tahmin kartları.</p>
        </div>

        <div className="basketball-live-page__actions">
          <button type="button" onClick={() => fetchLiveCards(false)} disabled={loading}>
            {loading ? 'Yükleniyor' : 'Yenile'}
          </button>
          <span>30 sn otomatik güncelleme</span>
          {lastUpdated && <span>Son: {lastUpdated.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
        </div>
      </div>

      {error && <div className="basketball-live-page__error">{error}</div>}
      {loading && visibleCards.length === 0 && <div className="basketball-live-page__empty">Canlı basketbol verileri yükleniyor.</div>}
      {!loading && !error && visibleCards.length === 0 && <div className="basketball-live-page__empty">Şu anda canlı basketbol maçı bulunamadı.</div>}

      {visibleCards.length > 0 && (
        <div className="basketball-live-page__grid">
          {visibleCards.map((card, index) => (
            <BasketballLiveCard card={card} key={card?.gameId || card?.id || `${card?.homeTeam}-${card?.awayTeam}-${index}`} />
          ))}
        </div>
      )}
    </section>
  );
};

export default BasketballLive;
